/**
 * Responsibility: say where a body of data lives, in two symbols.
 *
 * One page had chips reading "box + cloud" and another had nothing, which
 * meant reading a word to answer a question that is really two yes-or-nos:
 * is it on the server, and is it backed up. Two icons answer both at a
 * glance and answer them the same way everywhere.
 *
 * **Present is blue, absent is grey and struck through.** Not a missing
 * icon - an icon that is visibly not there. A row showing one symbol makes
 * you work out which one is missing; a row showing a struck-out cloud says
 * "not backed up" without being read.
 *
 * **Grey-struck is not always a fault, and the tooltip says which.** A
 * library archived and pruned from the box is *supposed* to have a struck
 * server icon - that is the archive working. A catalogue cache with a
 * struck cloud is a real gap. The colour cannot tell those apart, so the
 * words do.
 */
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import CloudIcon from '@mui/icons-material/CloudQueue';
import ServerIcon from '@mui/icons-material/DnsOutlined';
import type { SvgIconComponent } from '@mui/icons-material';

type Presence = {
  /** The bytes are on the Oracle box right now. */
  onServer: boolean;
  /** A backup holds them. */
  backedUp: boolean;
  /** What each state means here, since "absent" is sometimes correct. */
  serverNote?: string;
  cloudNote?: string;
};

/** One symbol, present or struck through, with its own explanation. */
function Mark({
  icon: Icon,
  present,
  note,
  label,
}: {
  icon: SvgIconComponent;
  present: boolean;
  note: string;
  label: string;
}) {
  return (
    <Tooltip title={note}>
      <Box
        aria-label={label}
        sx={{
          position: 'relative',
          display: 'inline-flex',
          color: present ? 'primary.main' : 'text.disabled',
        }}
      >
        <Icon fontSize="small" />
        {present ? null : (
          // Drawn across the icon rather than replacing it, so the shape
          // still reads as "cloud" and the state reads as "not that".
          <Box
            component="span"
            sx={{
              position: 'absolute',
              inset: 0,
              margin: 'auto',
              height: '1.5px',
              width: '118%',
              left: '-9%',
              bgcolor: 'text.disabled',
              transform: 'rotate(-45deg)',
              borderRadius: 1,
            }}
          />
        )}
      </Box>
    </Tooltip>
  );
}

export function StorageIcons({
  onServer,
  backedUp,
  serverNote,
  cloudNote,
}: Presence) {
  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Mark
        icon={ServerIcon}
        present={onServer}
        label={onServer ? 'On the server' : 'Not on the server'}
        note={
          serverNote ??
          (onServer ? 'Held on the Oracle box.' : 'Not on the box - pruned or never written.')
        }
      />
      <Mark
        icon={CloudIcon}
        present={backedUp}
        label={backedUp ? 'Backed up' : 'Not backed up'}
        note={
          cloudNote ?? (backedUp ? 'A backup holds this.' : 'No backup holds this yet.')
        }
      />
    </Stack>
  );
}

/**
 * The Data Hub's `location` string as the same two symbols.
 *
 * Its three values are a filesystem answer - asked of the disk rather than
 * assumed - and each maps onto the pair differently. `cloud` means archived
 * *and* pruned, which is the archive working as designed on a library too
 * big for a 50 GB box, so it is not shown as a loss.
 */
export function LocationIcons({ location }: { location: string }) {
  const onServer = location === 'oracle+cloud' || location === 'cloud+staging';
  const notes: Record<string, { server: string; cloud: string }> = {
    'oracle+cloud': {
      server: 'Every recorded image is still on the box.',
      cloud: 'Archived to Nextcloud.',
    },
    cloud: {
      server: 'Pruned from the box - by design. Too large to keep locally.',
      cloud: 'Nextcloud holds the only copy, verified before the local one was deleted.',
    },
    'cloud+staging': {
      server: 'Part-way through: some images pruned, some still here.',
      cloud: 'Archived to Nextcloud.',
    },
  };
  const note = notes[location];
  return (
    <StorageIcons
      onServer={onServer}
      backedUp
      serverNote={note?.server}
      cloudNote={note?.cloud}
    />
  );
}
